import { useState, useEffect, useCallback, useRef } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import PageHeader from "@/components/PageHeader";
import RichText from "@/components/guide/RichText";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Upload, FileText, Trash2, ExternalLink, Sparkles, Loader2, FileSpreadsheet, Image as ImageIcon, File as FileIcon, Library,
} from "lucide-react";

const CATEGORIES = ["Brand guide", "Style guide", "SOP", "Research", "Product draft", "Manuscript", "Marketing plan", "Website export", "General"];
const ANALYZE_ACTIONS = [
  { key: "summarize", label: "Summarize" },
  { key: "clarity", label: "Review clarity" },
  { key: "brand", label: "Brand consistency" },
  { key: "gaps", label: "Find gaps" },
  { key: "publish", label: "Prepare to publish" },
];
const ACCEPT = ".pdf,.docx,.xlsx,.xls,.txt,.md,.csv,.json,.png,.jpg,.jpeg,.webp,.gif";
const MAX = 25 * 1024 * 1024;

const iconFor = (ext) => {
  if (["xlsx", "xls", "csv"].includes(ext)) return FileSpreadsheet;
  if (["png", "jpg", "jpeg", "webp", "gif"].includes(ext)) return ImageIcon;
  if (["txt", "md", "json"].includes(ext)) return FileText;
  return FileIcon;
};
const fmtSize = (b) => (b > 1048576 ? (b / 1048576).toFixed(1) + " MB" : b > 1024 ? Math.round(b / 1024) + " KB" : b + " B");

export default function KnowledgeLibrary() {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [file, setFile] = useState(null);
  const [category, setCategory] = useState("General");
  const [notes, setNotes] = useState("");
  const [uploading, setUploading] = useState(false);
  const [docView, setDocView] = useState(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [analysis, setAnalysis] = useState("");
  const [deleteId, setDeleteId] = useState(null);
  const fileRef = useRef(null);

  const load = useCallback(async () => {
    try { const r = await api.get("/knowledge"); setDocs(r.data || []); }
    catch (e) { toast.error("Could not load your Knowledge Library."); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const doUpload = async () => {
    if (!file) { toast.error("Choose a file first."); return; }
    if (file.size > MAX) { toast.error("File is larger than 25 MB."); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("category", category);
      fd.append("notes", notes);
      await api.post("/knowledge/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Added to your Knowledge Library.");
      setUploadOpen(false); setFile(null); setNotes(""); setCategory("General");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed. Please try again.");
    } finally { setUploading(false); }
  };

  const openDoc = async (d) => {
    setAnalysis(""); setDocView({ ...d, text_preview: "" });
    try { const r = await api.get(`/knowledge/${d.id}`); setDocView(r.data); setAnalysis(r.data.summary || ""); }
    catch (e) { toast.error("Could not open that document."); }
  };

  const runAnalyze = async (action) => {
    setAnalyzing(true); setAnalysis("");
    try {
      const r = await api.post(`/knowledge/${docView.id}/analyze`, { action });
      setAnalysis(r.data.result || "");
    } catch (e) { toast.error(e?.response?.data?.detail || "Analysis failed."); }
    finally { setAnalyzing(false); }
  };

  const openFile = async (d) => {
    try {
      const r = await api.get(`/knowledge/${d.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) { toast.error("Could not open the file."); }
  };

  const remove = async () => {
    try { await api.delete(`/knowledge/${deleteId}`); setDocs((p) => p.filter((d) => d.id !== deleteId)); setDeleteId(null); setDocView(null); toast.success("Removed from library."); }
    catch (e) { toast.error("Could not remove."); }
  };

  return (
    <div>
      <PageHeader
        testid="knowledge-header"
        title="Knowledge Library"
        subtitle="Your permanent reference center. Upload brand guides, SOPs, research and drafts. The AI Business Guide learns your style and references them when helping you, without copying your work."
        action={<Button onClick={() => setUploadOpen(true)} data-testid="upload-button" className="rounded-full bg-primary hover:bg-primary/90 gap-2"><Upload className="h-4 w-4" /> Upload file</Button>}
      />

      {loading ? (
        <div className="flex justify-center py-20"><Sparkles className="h-6 w-6 text-primary animate-pulse" /></div>
      ) : docs.length === 0 ? (
        <Card className="rounded-3xl border-border shadow-soft p-12 text-center" data-testid="knowledge-empty">
          <div className="h-14 w-14 rounded-2xl bg-primary/10 flex items-center justify-center mx-auto mb-5"><Library className="h-7 w-7 text-primary" /></div>
          <h2 className="font-heading text-2xl mb-2">Build your reference center</h2>
          <p className="text-muted-foreground mb-6 max-w-md mx-auto">Upload PDFs, Word docs, spreadsheets and images. The Guide will read them and reference them in future conversations, reference-based, never copied.</p>
          <Button onClick={() => setUploadOpen(true)} data-testid="empty-upload-button" className="rounded-full bg-primary hover:bg-primary/90 gap-2"><Upload className="h-4 w-4" /> Upload your first file</Button>
        </Card>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="knowledge-list">
          {docs.map((d) => {
            const Icon = iconFor(d.ext);
            return (
              <button key={d.id} onClick={() => openDoc(d)} data-testid={`knowledge-doc-${d.id}`} className="text-left">
                <Card className="rounded-3xl border-border shadow-soft p-5 h-full hover:shadow-soft-lg hover:-translate-y-0.5 transition-all duration-300">
                  <div className="flex items-center justify-between mb-3">
                    <div className="h-10 w-10 rounded-2xl bg-primary/10 flex items-center justify-center"><Icon className="h-5 w-5 text-primary" strokeWidth={1.75} /></div>
                    <span className="text-[10px] uppercase tracking-wide px-2 py-0.5 rounded-full bg-accent/20 text-secondary">{d.category}</span>
                  </div>
                  <p className="font-medium truncate" title={d.original_filename}>{d.original_filename}</p>
                  <p className="text-xs text-muted-foreground mt-1">{d.ext?.toUpperCase()} · {fmtSize(d.size)}{d.text_len ? " · readable" : " · image"}</p>
                  {d.notes && <p className="text-xs text-muted-foreground mt-2 line-clamp-2">{d.notes}</p>}
                </Card>
              </button>
            );
          })}
        </div>
      )}

      {/* Upload dialog */}
      <Dialog open={uploadOpen} onOpenChange={setUploadOpen}>
        <DialogContent className="rounded-3xl sm:max-w-md" data-testid="upload-dialog">
          <DialogHeader><DialogTitle className="font-heading text-2xl">Upload to Knowledge Library</DialogTitle></DialogHeader>
          <DialogDescription>PDF, Word, Excel, text, CSV or images, up to 25 MB.</DialogDescription>
          <div className="space-y-4 py-2">
            <div>
              <input ref={fileRef} type="file" accept={ACCEPT} data-testid="file-input" className="hidden" onChange={(e) => setFile(e.target.files?.[0] || null)} />
              <button onClick={() => fileRef.current?.click()} data-testid="choose-file" className="w-full rounded-2xl border border-dashed border-border bg-muted/40 hover:bg-muted px-4 py-6 text-center transition-colors">
                <Upload className="h-6 w-6 text-primary mx-auto mb-2" />
                <p className="text-sm font-medium">{file ? file.name : "Choose a file"}</p>
                {file && <p className="text-xs text-muted-foreground mt-1">{fmtSize(file.size)}</p>}
              </button>
            </div>
            <div className="space-y-2"><Label>Category</Label>
              <Select value={category} onValueChange={setCategory}>
                <SelectTrigger className="rounded-xl" data-testid="upload-category"><SelectValue /></SelectTrigger>
                <SelectContent className="rounded-2xl">{CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-2"><Label>Notes <span className="text-muted-foreground font-normal">(optional)</span></Label>
              <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="What is this, and how should the Guide use it?" className="rounded-xl min-h-[64px]" data-testid="upload-notes" /></div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setUploadOpen(false)} className="rounded-full">Cancel</Button>
            <Button onClick={doUpload} disabled={uploading || !file} data-testid="confirm-upload" className="rounded-full bg-primary hover:bg-primary/90 gap-2">
              {uploading ? <><Loader2 className="h-4 w-4 animate-spin" /> Uploading…</> : "Upload"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Document dialog */}
      <Dialog open={!!docView} onOpenChange={(o) => !o && setDocView(null)}>
        <DialogContent className="rounded-3xl sm:max-w-3xl max-h-[88vh] overflow-hidden flex flex-col" data-testid="knowledge-doc-dialog">
          <DialogHeader><DialogTitle className="font-heading text-2xl pr-8 truncate">{docView?.original_filename}</DialogTitle></DialogHeader>
          <DialogDescription>{docView?.category} · {docView?.ext?.toUpperCase()} · {docView && fmtSize(docView.size)}</DialogDescription>

          <div className="flex flex-wrap items-center gap-2 py-2 border-b border-border">
            <Button variant="outline" size="sm" onClick={() => openFile(docView)} data-testid="doc-open-file" className="rounded-full border-border gap-1.5"><ExternalLink className="h-3.5 w-3.5" /> Open file</Button>
            {ANALYZE_ACTIONS.map((a) => (
              <Button key={a.key} variant="outline" size="sm" onClick={() => runAnalyze(a.key)} disabled={analyzing || !docView?.text_len} data-testid={`analyze-${a.key}`} className="rounded-full border-border gap-1.5">
                <Sparkles className="h-3.5 w-3.5 text-primary" /> {a.label}
              </Button>
            ))}
            <Button variant="outline" size="sm" onClick={() => setDeleteId(docView.id)} data-testid="doc-delete" className="rounded-full border-border gap-1.5 text-destructive hover:text-destructive ml-auto"><Trash2 className="h-3.5 w-3.5" /> Delete</Button>
          </div>

          <div className="overflow-y-auto flex-1 py-3 space-y-4">
            {analyzing && <div className="flex items-center gap-2 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> The Guide is reading this…</div>}
            {analysis && (
              <div className="rounded-2xl bg-primary/5 border border-primary/20 p-4" data-testid="doc-analysis">
                <p className="text-xs uppercase tracking-wide text-secondary mb-2">Guide notes</p>
                <RichText text={analysis} />
              </div>
            )}
            {!docView?.text_len ? (
              <p className="text-sm text-muted-foreground">This is an image or non-text file. It's stored as reference; open it above to view. (Text reading isn't available for images yet.)</p>
            ) : (
              <div>
                <p className="text-xs uppercase tracking-wide text-muted-foreground mb-2">Extracted text</p>
                <div className="rounded-2xl bg-muted/30 p-4 text-sm whitespace-pre-wrap leading-relaxed max-h-[40vh] overflow-y-auto" data-testid="doc-text">{docView?.text_preview || "…"}</div>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteId} onOpenChange={(o) => !o && setDeleteId(null)}>
        <AlertDialogContent className="rounded-3xl">
          <AlertDialogHeader><AlertDialogTitle className="font-heading">Remove this document?</AlertDialogTitle>
            <AlertDialogDescription>It will be removed from your Knowledge Library and the Guide will no longer reference it.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter><AlertDialogCancel className="rounded-full">Keep it</AlertDialogCancel>
            <AlertDialogAction onClick={remove} data-testid="confirm-delete-doc" className="rounded-full bg-destructive hover:bg-destructive/90">Remove</AlertDialogAction></AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
